import React, { useEffect } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { useAppDispatch } from "src/common/state/hooks";
import Chatbot from "src/features/chatbot/components/Chatbot";
import GptConversationLoader, {
  GPT_CONVERSATION_LOADER_COMPONENT_ID,
} from "src/features/chatbot/components/GptConversationLoader";
import RagAgentMultiSelect from "src/features/rag/components/RagAgentMultiSelect";
import {
  fetchAgentsThunk,
  getRagAgents,
  getRagAgentsStatus,
} from "src/features/rag/state/ragSlice";
import {
  setActiveGptConversation,
  getGptConversationsSortedByRecency,
  getActiveGptConversation,
  fetchGptConversations,
  resetChat,
  getScenarioHelperRagAgentIds,
  setScenarioHelperRagAgentIds,
} from "src/features/chatbot/state/chatbotSlice";

const ScenarioHelperChat: React.FC = () => {
  const dispatch = useAppDispatch();
  const userPreviousGptConversations = useSelector(getGptConversationsSortedByRecency);
  const userActiveGptConversation = useSelector(getActiveGptConversation);
  const ragAgentIds = useSelector(getScenarioHelperRagAgentIds, shallowEqual);
  const agents = useSelector(getRagAgents);
  const agentsStatus = useSelector(getRagAgentsStatus);

  const handleConversationSelect = (conversationId: number) => {
    const selectedConversation = userPreviousGptConversations.find(
      (conv) => conv.id === conversationId,
    );
    if (selectedConversation) {
      dispatch(setActiveGptConversation(selectedConversation));
    }
  };

  const preSelectSophieIfAvailable = () => {
    const sophie = agents.find((a) => a.is_shared && a.name.toLowerCase() === "sophie");
    if (sophie) {
      dispatch(setScenarioHelperRagAgentIds([sophie.id]));
    }
  };

  const handleResetChat = () => {
    dispatch(resetChat({ cb: resetChatLocally })).then(() => {
      dispatch(
        fetchGptConversations({ componentId: GPT_CONVERSATION_LOADER_COMPONENT_ID, start: 0 }),
      );
      // Re-apply the default Sophie selection after the reset. Chatbot's
      // resetChatLocally fires synchronously on activeConversation→null and
      // clears scenarioHelperRagAgentIds; doing the pre-select inside the
      // .then() guarantees we land after that clear regardless of effect
      // ordering.
      preSelectSophieIfAvailable();
    });
  };

  const resetChatLocally = () => {
    setActiveGptConversation(null);
  };

  useEffect(() => {
    dispatch(
      fetchGptConversations({ componentId: GPT_CONVERSATION_LOADER_COMPONENT_ID, start: 0 }),
    );
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchAgentsThunk());
  }, [dispatch]);

  // Pre-select the shared "Sophie" Wixar agent on initial mount with no active
  // conversation. The "Nouvelle requête" path handles its own pre-select inline
  // (see handleResetChat) — this effect only covers first-load. Existing
  // conversations restore their own rag_agent_ids via Chatbot's
  // loadPreviousConversation, so skip when a conv is already active.
  useEffect(() => {
    if (userActiveGptConversation) return;
    if (agentsStatus !== "loaded") return;
    preSelectSophieIfAvailable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userActiveGptConversation, agentsStatus, agents]);

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <div
        className="grid min-h-0 w-full flex-1 overflow-hidden"
        style={{ gridTemplateColumns: "240px 1fr 280px" }}
      >
        <div className="bg-gray-100 px-3 pt-1 border-r border-gray-200 overflow-y-auto overflow-x-hidden">
          <GptConversationLoader
            onConversationSelect={handleConversationSelect}
            onResetChat={handleResetChat}
          />
        </div>

        <div className="min-w-0 overflow-hidden">
          <Chatbot mode="scenarioHelper" activeConversation={userActiveGptConversation} />
        </div>

        <div className="bg-gray-50 border-l border-gray-200 p-4 pb-32 flex flex-col min-h-0 overflow-x-hidden overflow-y-hidden">
          <RagAgentMultiSelect
            fillHeight
            value={ragAgentIds}
            onChange={(ids) => dispatch(setScenarioHelperRagAgentIds(ids))}
          />
        </div>
      </div>
    </div>
  );
};

export default ScenarioHelperChat;
